import { useState } from 'react';
import { Button, Card, Field, Input } from '@bala/ui';
import { api } from '../api';
import { useAuth } from '../auth';
import { AuthLayout } from './AuthLayout';

/**
 * Première installation : crée le compte Baygon initial.
 *
 * L'écran n'apparaît que si la base ne contient aucun animateur — le serveur
 * refuse la route dès qu'un compte existe, ce qui évite d'avoir à protéger
 * l'accès à cette page autrement.
 */
export function BootstrapPage() {
  const auth = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(
        '/auth/bootstrap',
        { firstName, lastName, email, password },
        { queueOffline: false },
      );
      await auth.refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "L'installation a échoué.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Installation de Bala’App"
      subtitle="Créez le premier compte Baygon. Vous pourrez ensuite inviter le reste du staff."
    >
      <Card padding="lg">
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
          <div className="bala-form-row">
            <Field label="Prénom" htmlFor="firstName" required>
              <Input
                id="firstName"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                autoComplete="given-name"
                autoFocus
                required
              />
            </Field>
            <Field label="Nom" htmlFor="lastName" required>
              <Input
                id="lastName"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                autoComplete="family-name"
                required
              />
            </Field>
          </div>

          <Field label="Adresse email" htmlFor="email" required hint="Elle servira à vous connecter.">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
          </Field>

          <Field label="Mot de passe" htmlFor="password" required hint="Au moins 8 caractères, une lettre et un chiffre.">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>

          <Field label="Confirmation" htmlFor="confirm" required>
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>

          {error && (
            <p
              role="alert"
              style={{
                font: 'var(--t-footnote)',
                color: 'var(--red)',
                background: 'var(--red-soft)',
                padding: 'var(--s-3)',
                borderRadius: 'var(--r-md)',
              }}
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            Créer le compte Baygon
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}
